'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { CONSENT_STORAGE_KEY, buildConsentState, type ConsentChoice } from '@/lib/consent'
import { clearPersistentAttribution, persistAttribution, pushDataLayer } from '@/lib/lead-tracking-client'

type GtagWindow = Window & { gtag?: (...args: unknown[]) => void; dataLayer?: unknown[] }

const COPY = {
  es: {
    text: 'Usamos cookies de Google para analítica y medir campañas.',
    accept: 'Aceptar',
    reject: 'Rechazar',
    policy: 'Política de cookies',
    policyHref: '/cookies',
  },
  en: {
    text: 'We use Google cookies for analytics and campaign measurement.',
    accept: 'Accept',
    reject: 'Reject',
    policy: 'Cookie policy',
    policyHref: '/en/cookies',
  },
} as const

function updateConsent(choice: ConsentChoice) {
  const w = window as GtagWindow
  const state = buildConsentState(choice)
  if (typeof w.gtag !== 'function') {
    // Normally defined by CONSENT_DEFAULT_SCRIPT; GTM only reads consent commands pushed as `arguments`.
    w.dataLayer = w.dataLayer || []
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments)
    }
  }
  w.gtag('consent', 'update', state)
  pushDataLayer({ event: 'cookie_consent_update', consent: choice })
}

export default function CookieConsentBanner() {
  const pathname = usePathname() || '/'
  const [visible, setVisible] = useState(false)
  const copy = pathname === '/en' || pathname.startsWith('/en/') ? COPY.en : COPY.es

  useEffect(() => {
    try {
      setVisible(!window.localStorage.getItem(CONSENT_STORAGE_KEY))
    } catch {
      setVisible(true)
    }
  }, [])

  const choose = (choice: ConsentChoice) => {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, choice)
    } catch {
      // Storage blocked: the choice applies to this page view only.
    }
    updateConsent(choice)
    if (choice === 'granted') persistAttribution()
    else clearPersistentAttribution()
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={copy.policy}
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-700 bg-gray-950 px-4 py-2 shadow-2xl"
    >
      {/* Compact on purpose: a large late-painted banner becomes the mobile LCP element */}
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 text-xs text-gray-300">
        <p>
          {copy.text}{' '}
          <a href={copy.policyHref} className="underline hover:text-white">
            {copy.policy}
          </a>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => choose('denied')}
            className="rounded-lg border border-gray-600 px-3 py-1.5 font-semibold text-gray-200 hover:border-gray-400"
          >
            {copy.reject}
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className="rounded-lg bg-amber-400 px-3 py-1.5 font-bold text-gray-900 hover:bg-amber-300"
          >
            {copy.accept}
          </button>
        </div>
      </div>
    </div>
  )
}
